import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'

import social from '@/assets/social.png?url'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { findEvents } from '@/models/event.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'
import { partition } from '@/utils/enumerator'
import { eventLabel } from '@/utils/events'

type LoaderData = typeof loader

export function loader({ url }: LoaderFunctionArgs) {
  const page = pageHelper(url)

  const events = findEvents()
  const { root } = page

  return {
    events: events,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const { root } = loaderData

  const title = 'Events — PlanetScale'
  const description = 'Join us at upcoming PlanetScale events, conferences, and meetups.'
  const image = `${root}${social}`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:url', content: 'https://planetscale.com/events' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: 'https://planetscale.com/events' },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: 'https://planetscale.com/events' }
  ]
}

export default function EventsIndex() {
  const { events } = useLoaderData<LoaderData>()

  // oxlint-disable-next-line react/purity -- upcoming vs. past events intentionally depend on the current time
  const now = new Date()
  const [upcoming, past] = partition(events, (event) => new Date(event.createdAt) >= now)
  const [nextEvent, ...upcomingEvents] = [...upcoming].reverse()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <section>
          <h1>Events</h1>

          <p className='mb-4 text-secondary'>
            Join us at upcoming PlanetScale meetups, livestreams, conferences, and parties.
          </p>

          {nextEvent && (
            <>
              <h2>Upcoming Events</h2>

              <Link prefetch='intent' to={`/events/${nextEvent.slug}`} className='block border p-3 hover:bg-focus'>
                <div className='mb-2 flex flex-col flex-wrap gap-y-1 lg:flex-row lg:justify-between'>
                  <div className='flex flex-wrap gap-x-2 gap-y-1'>
                    <h3 className='mb-0 font-semibold'>
                      <span className='bg-gray-800 box-decoration-clone px-1 py-sm text-white dark:bg-gray-100 dark:text-black'>
                        {nextEvent.title}
                      </span>
                    </h3>
                    <span className='font-medium text-green'>{eventLabel(nextEvent.category)}</span>
                  </div>

                  <div className='flex justify-start gap-1 text-secondary lg:justify-end'>
                    <time dateTime={nextEvent.createdAt} className='mb-1 lg:mb-0'>
                      {formatLong(nextEvent.createdAt)}
                    </time>
                    {nextEvent.location && (
                      <>
                        <span>&middot;</span>
                        <span className='mb-1 lg:mb-0'>{nextEvent.location}</span>
                      </>
                    )}
                  </div>
                </div>

                <p className='text-secondary'>{nextEvent.description}</p>
              </Link>
            </>
          )}

          {upcomingEvents.length > 0 && (
            <div className='mt-5'>
              {upcomingEvents.map((event) => (
                <Link
                  prefetch='intent'
                  to={`/events/${event.slug}`}
                  key={event.slug}
                  className='block py-1 hover:bg-focus'
                >
                  <div className='flex flex-col justify-between lg:flex-row'>
                    <div className='flex flex-wrap items-baseline gap-2 lg:mb-0'>
                      <h3 className='mb-0 text-secondary'>{event.title}</h3>
                      <span className='font-medium text-green'>{eventLabel(event.category)}</span>
                    </div>
                    <div className='flex space-x-1 text-secondary'>
                      <time dateTime={event.createdAt} className='mb-1 lg:mb-0'>
                        {formatLong(event.createdAt)}
                      </time>
                      {event.location && (
                        <>
                          <span>&middot;</span>
                          <span className='mb-1 lg:mb-0'>{event.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {past.length > 0 && (
            <>
              <h2 className='mt-6'>Past Events</h2>

              {past.map((event) => (
                <Link
                  prefetch='intent'
                  to={`/events/${event.slug}`}
                  key={event.slug}
                  className='block py-1 hover:bg-focus'
                >
                  <div className='flex flex-col justify-between lg:flex-row'>
                    <div className='flex flex-wrap items-baseline gap-2 lg:mb-0'>
                      <h3 className='mb-0 text-secondary'>{event.title}</h3>
                      <span className='font-medium text-green'>{eventLabel(event.category)}</span>
                    </div>
                    <div className='flex space-x-1 text-secondary'>
                      <time dateTime={event.createdAt} className='mb-1 lg:mb-0'>
                        {formatLong(event.createdAt)}
                      </time>
                      {event.location && (
                        <>
                          <span>&middot;</span>
                          <span className='mb-1 lg:mb-0'>{event.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </>
          )}

          {events.length === 0 && (
            <div className='py-12 text-center'>
              <p>No events scheduled at this time.</p>
              <p className='text-secondary'>Check back soon for upcoming PlanetScale events!</p>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
